
import React from 'react';
import { Button } from '@/components/ui/button';
import { MessageSquare } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { GridBackground } from '@/components/ui/grid-background';
import { GlowEffect } from '@/components/ui/glow-effect';

interface CtaSectionProps {
  title?: string;
  highlightText?: string;
  description?: string;
  primaryButtonText?: string;
  primaryButtonLink?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
}

const CtaSection = ({
  title,
  highlightText,
  description,
  primaryButtonText = "How We Do It",
  primaryButtonLink = "/services/process",
  secondaryButtonText = "Book a Call",
  secondaryButtonLink = "/booking",
}: CtaSectionProps) => {
  const location = useLocation();
  const isProcessPage = location.pathname === "/services/process";
  
  // Set default values based on current page
  const defaultTitle = isProcessPage 
    ? "Don't see your" 
    : "Ready to Build, Secure, or";
  
  const defaultHighlight = isProcessPage 
    ? "industry?" 
    : "Automate with AI?";
  
  const defaultDescription = isProcessPage 
    ? "We can build custom AI-powered solutions for any business." 
    : "Book a free consultation and discover how we can help you build faster, secure smarter, and automate everything—all under $5k.";
  
  // Use the props if provided, otherwise use the default values
  const displayTitle = title || defaultTitle;
  const displayHighlight = highlightText || defaultHighlight;
  const displayDescription = description || defaultDescription;
  
  return (
    <section className="py-12 sm:py-16 px-4 sm:px-6 md:px-10">
      <div className="container mx-auto">
        <GridBackground
          title={`${displayTitle} ${displayHighlight}`}
          description={displayDescription}
          className="rounded-xl overflow-hidden shadow-luxury"
        >
          <div className="flex flex-col sm:flex-row items-center justify-center mt-7 gap-5">
            <div className="relative w-full sm:w-auto group">
              {/* Glow effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <GlowEffect
                  colors={['#c8a87d', '#e6d5b8', '#a68756']}
                  mode="breathe"
                  blur="soft"
                  scale={1.2}
                  duration={3}
                />
              </div>
              
              <Link to={primaryButtonLink} className="w-full sm:w-auto block" onClick={() => window.scrollTo(0, 0)}>
                <Button 
                  starBorder
                  size="lg" 
                  className="w-full sm:w-auto font-cta relative z-10 flex items-center justify-center"
                >
                  <span>{primaryButtonText}</span>
                </Button>
              </Link>
            </div>
            
            <Link to={secondaryButtonLink} className="w-full sm:w-auto block" onClick={() => window.scrollTo(0, 0)}>
              <Button 
                variant="outline"
                size="lg" 
                className="w-full sm:w-auto font-cta group flex items-center justify-center"
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                {secondaryButtonText}
              </Button>
            </Link>
          </div>
        </GridBackground>
      </div>
    </section>
  );
};

export default CtaSection;
